import dayjs from "dayjs";

export const MODE_SCHEDULER_GRAPH = "scheduler-graph";
export const MODE_SCHEDULER_TIMELINE = "scheduler-timeline";
export const MODE_THROUGHPUT = "throughput";
export const MODE_DAILY = "daily";

const MAX_WEEKS = 6;

const CONFIG = {
  [MODE_SCHEDULER_GRAPH]: {
    weekStartDay: 0,
    snap: true,
    maxWeeks: MAX_WEEKS,
    defaultWeeks: MAX_WEEKS,
  },
  [MODE_SCHEDULER_TIMELINE]: {
    weekStartDay: null,
    snap: false,
    // Unsnapped, ranges-of-months — defaults handled separately.
  },
  [MODE_THROUGHPUT]: {
    weekStartDay: 3,
    snap: true,
    maxWeeks: MAX_WEEKS,
    defaultWeeks: MAX_WEEKS,
  },
  [MODE_DAILY]: {
    // Daily view picks a calendar quarter rather than a week range,
    // so it doesn't participate in the week-snap or maxWeeks logic.
    rangeType: "quarter",
    snap: false,
  },
};

export const DATE_FORMAT = "YYYY-MM-DD";

export const getDateConfig = (mode) =>
  CONFIG[mode] || CONFIG[MODE_SCHEDULER_TIMELINE];

const startOfWeek = (date, weekStartDay) => {
  const d = dayjs(date);
  const day = d.day();
  const diff =
    day >= weekStartDay ? day - weekStartDay : day + 7 - weekStartDay;
  return d.subtract(diff, "day").startOf("day");
};

const endOfWeek = (date, weekStartDay) =>
  startOfWeek(date, weekStartDay).add(6, "day");

const fmt = (d) => dayjs(d).format(DATE_FORMAT);

// Normalize whatever the picker hands us (Date, dayjs, ISO string,
// "YYYY-MM-DD") into a date-only string.
export const toDateString = (val) => {
  if (!val) return null;
  const d = dayjs(val);
  return d.isValid() ? fmt(d) : null;
};

// Convert a date-only "YYYY-MM-DD" to the ISO string the picker
// component expects (it does `dayjs.utc(value).local()` internally,
// so it needs the local-midnight-as-UTC representation, which is
// exactly what `.toISOString()` of a local dayjs produces).
export const toPickerValue = (dateString) =>
  dateString ? dayjs(dateString).toISOString() : null;

export const snapStart = (date, mode) => {
  const cfg = getDateConfig(mode);
  if (!cfg.snap) return fmt(date);
  return fmt(startOfWeek(date, cfg.weekStartDay));
};

export const snapEnd = (date, mode) => {
  const cfg = getDateConfig(mode);
  if (!cfg.snap) return fmt(date);
  return fmt(endOfWeek(date, cfg.weekStartDay));
};

// ── Quarter helpers ──

// dayjs has no quarter accessor without a plugin; derive it from month.
const quarterOf = (d) => Math.floor(d.month() / 3) + 1;

export const getCurrentQuarter = () => {
  const now = dayjs();
  return { year: now.year(), quarter: quarterOf(now) };
};

export const getQuarterFromDate = (date) => {
  if (!date) return getCurrentQuarter();
  const d = dayjs(date);
  return { year: d.year(), quarter: quarterOf(d) };
};

export const getQuarterRange = (year, quarter) => {
  const startMonth = (quarter - 1) * 3;
  const start = dayjs(new Date(year, startMonth, 1));
  const end = start.add(3, "month").subtract(1, "day");
  return {
    startDate: fmt(start),
    endDate: fmt(end),
    label: `Q${quarter} ${year}`,
    year,
    quarter,
  };
};

// Compute defaults relative to today (always recomputed at render
// time so a long-open tab doesn't get stuck on a stale "today").
export const getDefaultRange = (mode) => {
  const cfg = getDateConfig(mode);
  const today = dayjs();

  if (cfg.rangeType === "quarter") {
    const { year, quarter } = getCurrentQuarter();
    return getQuarterRange(year, quarter);
  }

  if (!cfg.snap) {
    const start = today.subtract(1, "month").startOf("month");
    const end = today.add(3, "month").endOf("month");
    return { startDate: fmt(start), endDate: fmt(end) };
  }

  const start = startOfWeek(today, cfg.weekStartDay).subtract(1, "week");
  const weeks = cfg.defaultWeeks || cfg.maxWeeks || 6;
  const end = start.add(weeks - 1, "week").add(6, "day");
  return { startDate: fmt(start), endDate: fmt(end) };
};

// When the user changes one end of a locked range (chart mode),
// adjust the other end so the range stays exactly maxWeeks long.
export const onStartChange = (date, mode) => {
  const cfg = getDateConfig(mode);
  const start = cfg.snap
    ? startOfWeek(date, cfg.weekStartDay)
    : dayjs(date).startOf("day");
  if (!cfg.maxWeeks) return { startDate: fmt(start) };
  const end = start.add(cfg.maxWeeks - 1, "week").add(6, "day");
  return { startDate: fmt(start), endDate: fmt(end) };
};

export const onEndChange = (date, mode) => {
  const cfg = getDateConfig(mode);
  const end = cfg.snap
    ? endOfWeek(date, cfg.weekStartDay)
    : dayjs(date).startOf("day");
  if (!cfg.maxWeeks) return { endDate: fmt(end) };
  const start = end.subtract(cfg.maxWeeks - 1, "week").subtract(6, "day");
  return { startDate: fmt(start), endDate: fmt(end) };
};

// Picker shouldDisableDate predicates — forbid days that aren't on
// the relevant week boundary so the displayed value can never drift
// off-snap.
export const startDateDisabler = (mode) => {
  const cfg = getDateConfig(mode);
  if (!cfg.snap || cfg.weekStartDay == null) return undefined;
  return (d) => dayjs(d).day() !== cfg.weekStartDay;
};

export const endDateDisabler = (mode) => {
  const cfg = getDateConfig(mode);
  if (!cfg.snap || cfg.weekStartDay == null) return undefined;
  const endDay = (cfg.weekStartDay + 6) % 7;
  return (d) => dayjs(d).day() !== endDay;
};

// Used by hooks so the query agrees with whatever the bar shows.
export const resolveRange = (searchParams, mode) => {
  const defaults = getDefaultRange(mode);
  const startDate = toDateString(searchParams.get("startDate")) || defaults.startDate;
  const endDate = toDateString(searchParams.get("endDate")) || defaults.endDate;
  return { startDate, endDate };
};
